import * as Y from 'yjs';
import { yUndoPluginKey } from '@tiptap/y-tiptap';
import type { UndoPluginState } from 'y-prosemirror';
import AI from '@tiptap-pro/extension-ai';
import CommentsKit from '@tiptap-pro/extension-comments';
import { EditorEvents, Extension, JSONContent } from '@tiptap/core';
import BubbleMenu from '@tiptap/extension-bubble-menu';
import CharacterCount from '@tiptap/extension-character-count';
import { CodeBlockLowlight } from '@tiptap/extension-code-block-lowlight';
import Collaboration from '@tiptap/extension-collaboration';
import CollaborationCursor from '@tiptap/extension-collaboration-caret';
import Document from '@tiptap/extension-document';
import History from '@tiptap/extension-history';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import Text from '@tiptap/extension-text';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import Typography from '@tiptap/extension-typography';
import { EditorProps } from '@tiptap/pm/view';
import {
  Content,
  Editor,
  Extensions,
  ReactNodeViewRenderer,
  useEditor,
  UseEditorOptions,
} from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { AiToolkit } from '@tiptap-pro/ai-toolkit';
import { anchorTypes } from 'components/TableOfContents/const';
import { common, createLowlight } from 'lowlight';
import { TSessionConfigData } from 'providers/contexts/CosmosContext';
import { AppSettingsCtxType } from 'providers/contexts/SettingsContext';
import { DependencyList, useCallback, useEffect, useMemo, useState } from 'react';
import { clone, isNumber } from 'remeda';
import {
  CollabProviderState,
  NXT_YDOC_CONFIG,
  SyncedInitialYDocConfigState,
} from 'screens/editorial/asset/context/AssetConfigUtilities';
import { CollabUserInfo } from 'screens/editorial/asset/presence/useDocumentPresence';
import {
  ARTICLE_BODY_COPY_ID,
  EditorSetupConfig,
} from 'screens/editorial/asset/utility/getEditorConfigs';
import {
  hasCollabExp,
  hasCommentsExp,
  hasStyleGuideExp,
  hasBetaProgram,
} from 'utility/experiments';
import { FullEditorialAsset } from 'utility/types/dataProvider';
import { YKeyValue } from 'y-utility/y-keyvalue';
import { BloxInline } from 'screens/editorial/asset/components/article/InlineAssets/extensions/BloxInline';
import CodeBlockComponent from '../extensions/Codeblock/CodeBlockComponent';
import { ColonCommand } from '../extensions/ColonCommands';
import CustomParagraph from '../extensions/CustomParagraph';
import { DisableCtrlEnter } from '../extensions/DisableCtrlEnter';
import { HorizontalRuleMulti } from '../extensions/HorizontalRuleMulti';
import { InlineNote } from '../extensions/InlineNote';
import { PrintTrim } from '../extensions/PrintTrim';
import { SearchAndReplace } from '../../SearchAndReplaceMenu/extension/SearchAndReplace';
import { SingleLine } from '../extensions/SingleLine';
import { SlashCommand } from '../extensions/SlashCommand';
import { TableCaption } from '../extensions/Table/TableCaption';
import TableExtended from '../extensions/Table/TableExtended';
import { TableRowExtended } from '../extensions/Table/TableRowExtended';
import { TableOfContentsExtended } from '../extensions/TableOfContentsExtended';
import { UnderlineExtended } from '../extensions/UnderLineExtended';
import { extractCoordinate } from 'utility/functions';

interface GetEditorExtensionsParams {
  editorConfig?: EditorSetupConfig;
  sessionConfig?: TSessionConfigData;
  // -- collab props --
  collabProvider?: CollabProviderState;
  collabUser?: CollabUserInfo;
  appSettings?: AppSettingsCtxType['appSettings'];
}

const CommentsLifecycleGuard = Extension.create({
  name: 'commentsLifecycleGuard',
  onDestroy() {
    // extension-comments debounces work on transactions; clear pending callbacks
    // so they cannot execute after editor teardown with a null editor reference.
    const commentsStorage = this.editor?.storage?.comments;
    const debounceTimer = commentsStorage?.debounceTimer;
    if (debounceTimer) {
      clearTimeout(debounceTimer);
      commentsStorage.debounceTimer = undefined;
    }
  },
});

// Configure editor extensions
export const getEditorExtensions = ({
  editorConfig,
  collabProvider,
  collabUser,
  sessionConfig,
  appSettings,
}: GetEditorExtensionsParams) => {
  const name = editorConfig?.name;
  const type = editorConfig?.type;
  const placeholder = editorConfig?.placeholder;
  const maxLength = editorConfig?.maxLength;
  const disableDropCursor = editorConfig?.disableDropCursor ?? false;
  const serviceKeys = sessionConfig?.service_keys;
  const hasCollabDocument = Boolean(
    collabProvider?.document && !collabProvider?.document?.isDestroyed,
  );
  const includeHistory = !collabProvider;
  const siteSettings: AppSettingsCtxType['appSettings']['site'] = appSettings?.site || {};
  const isAIEnabled =
    siteSettings?.editorial?.content_extensions &&
    Boolean(serviceKeys?.tiptap_ai_app && serviceKeys?.tiptap_ai_token);

  const isCommentsEnabled =
    hasCommentsExp(sessionConfig) && hasCollabExp(sessionConfig) && hasCollabDocument;

  const isStyleGuideEnabled =
    hasStyleGuideExp(sessionConfig) && hasBetaProgram(sessionConfig);

  // For html code editor specifically we fake a codemirror style
  // so add in specific extensions and return the reduced set
  // no starter kit, all extensions specific to html editor
  if (type === 'code') {
    const codeExts: Extensions = [];
    // Basic extensions
    codeExts.push(Document);
    codeExts.push(Text);
    codeExts.push(History);
    codeExts.push(DisableCtrlEnter);
    codeExts.push(
      Placeholder.configure({
        placeholder: placeholder,
        showOnlyWhenEditable: false,
      }),
    );
    codeExts.push(CharacterCount.configure({ limit: maxLength }));
    // https://stackoverflow.com/questions/77573628/how-do-you-highlight-codeblock-with-tiptap
    codeExts.push(
      CodeBlockLowlight.extend({
        addNodeView() {
          return ReactNodeViewRenderer(CodeBlockComponent);
        },
      }).configure({
        lowlight: createLowlight(common),
        exitOnArrowDown: false,
        exitOnTripleEnter: false,
        defaultLanguage: 'html',
      }),
    );
    // return early
    return codeExts;
  }

  // If type is hidden, we will generally not mount the editor ui
  // and its main purpose is to sync data around the ydoc as needed
  if (type === 'hidden') {
    const hiddenExts: Extensions = [];
    hiddenExts.push(Document);
    hiddenExts.push(Text);
    hiddenExts.push(CustomParagraph);
    if (collabProvider && name && hasCollabDocument) {
      hiddenExts.push(
        Collaboration.configure({
          document: collabProvider?.document,
          field: name,
        }),
      );
    }
    // return early
    return hiddenExts;
  }

  // --- set up extensions ---
  const extensions: Extensions = [];

  // Basic extensions
  extensions.push(Document);
  extensions.push(Text);
  extensions.push(Typography);
  extensions.push(TextStyle);
  extensions.push(CustomParagraph);
  extensions.push(CharacterCount.configure({ limit: maxLength }));
  extensions.push(
    Placeholder.configure({
      placeholder: placeholder,
      showOnlyWhenEditable: false,
    }),
  );

  // Add history extension if not in collaboration mode
  if (includeHistory) {
    extensions.push(History);
  }

  /**
   * Setup collab exts if provider and given a field name for the yjs fragment
   * note: once collaboration is given to the editor it will do an initial
   * set content call via the ydoc which does emit an txn event...
   *
   * 20260825 - CMSADMIN-1563 Added collab exclusion for inline editors,
   * as they are not supported in collab mode
   */

  if (collabProvider && name && hasCollabDocument && type !== 'inline') {
    extensions.push(
      Collaboration.configure({
        document: collabProvider?.document,
        field: name,
      }),
    );
    extensions.push(
      CollaborationCursor.configure({
        provider: collabProvider,
        user: collabUser,
      }),
    );
  }

  // --- Specific editor type extensions ---

  // Single line input types
  if (type === 'text' || type === 'number' || type === 'coordinate') {
    extensions.push(SingleLine);
  }

  /**
   * -----------------------------------------------------------
   * per discussion with Phil on richtext (specifically tagline)
   * inconsistencies between blue and nxt:
   * In blue can paste h1-h6 but theres no formatting option in the blue toolbar.
   * Nxt we wont add heading level ext so preexisting h1-h6 would show as paragraph
   * In blue table and horizontal rule is supported so for NXT we will
   * add those in the richtext exts so they can render, but deciding whether
   * to show the formatting button is up to EditorBar.tsx / EditorWithBar.tsx
   * -----------------------------------------------------------
   *
   * StarterKit notes:
   *
   * note: tiptap v2 StarterKits did not have trailingNode ext.
   * tiptap v3 default: auto inserts empty <p> to end of doc.
   * Disabling the extension here in StarterKits and additionally
   * sanitizing in cleanupTiptapHtml function, as the ext.
   * can cause false positive autosave diff checks and does
   * not represent content structure as provided by the api record.
   * https://tiptap.dev/docs/editor/extensions/functionality/starterkit
   * https://tiptap.dev/docs/editor/extensions/functionality/trailing-node
   *
   */

  // Richtext type extensions
  if (type === 'richtext') {
    const isBylineField = name === 'byline'; // TNCMS-556561 API limits byline to specific formatting, so we disable some extensions for byline fields

    extensions.push(
      StarterKit.configure({
        document: false,
        undoRedo: false,
        paragraph: false,
        text: false,
        heading: false,
        link: false,
        underline: false,
        bulletList: isBylineField ? false : undefined,
        orderedList: isBylineField ? false : undefined,
        blockquote: isBylineField ? false : undefined,
        horizontalRule: isBylineField ? false : undefined,
        dropcursor: disableDropCursor ? false : undefined,
        trailingNode: false, // see StarterKit notes
      }),
    );

    if (!isBylineField) {
      extensions.push(ColonCommand);
      extensions.push(TextAlign.configure({ types: ['paragraph'] }));
      extensions.push(TableExtended.configure({ resizable: false }));
      extensions.push(TableRowExtended);
      extensions.push(TableCaption);
      extensions.push(TableHeader);
      extensions.push(TableCell);
    }

    extensions.push(
      Link.configure({
        openOnClick: false,
        autolink: false, // disable autolink
        HTMLAttributes: {
          rel: 'noopener noreferrer nofollow', // Security best practice
        },
      }),
    );
    extensions.push(
      BubbleMenu.configure({
        updateDelay: 500,
      }),
    );

    extensions.push(UnderlineExtended);

    if (name === 'properties.tagline') {
      extensions.push(
        SearchAndReplace.configure({
          searchResultClass: 'search-result',
          disableRegex: false,
        }),
      );
    }
  }

  if (type === 'inline' && name === 'byline') {
    extensions.push(
      Link.configure({
        openOnClick: false,
        autolink: false,
        HTMLAttributes: {
          rel: 'noopener noreferrer nofollow',
        },
      }),
    );
    extensions.push(
      BubbleMenu.configure({
        updateDelay: 500,
      }),
    );
  }

  // Body type extensions (main: article bodycopy)
  if (type === 'body') {
    extensions.push(
      StarterKit.configure({
        document: false,
        undoRedo: false,
        paragraph: false,
        text: false,
        horizontalRule: false,
        link: false,
        underline: false,
        dropcursor: disableDropCursor ? false : undefined,
        trailingNode: false,
      }),
    );

    // Add AI extension if enabled
    if (isAIEnabled) {
      const hasTabAutocomplete = editorConfig?.id === ARTICLE_BODY_COPY_ID;
      extensions.push(
        AI.configure({
          appId: serviceKeys?.tiptap_ai_app,
          token: serviceKeys?.tiptap_ai_token,
          autocompletion: hasTabAutocomplete,
        }),
      );
    }

    if (isStyleGuideEnabled) {
      extensions.push(AiToolkit);
    }

    // Comments extension if enabled
    if (isCommentsEnabled && collabProvider) {
      extensions.push(
        CommentsKit.configure({
          provider: collabProvider,
        }),
      );
      extensions.push(CommentsLifecycleGuard);
    }

    extensions.push(
      TableOfContentsExtended.configure({
        anchorTypes: anchorTypes,
        allowEmptyNodes: true,
      }),
    );
    extensions.push(TextAlign.configure({ types: ['heading', 'paragraph'] }));
    extensions.push(InlineNote);
    extensions.push(PrintTrim);
    extensions.push(BloxInline);

    /**
     * CMSADMIN-1001 - legacy embedded images
     *
     * API will wrap loose <img> tags in <p>
     * tags as it forces block level elements.
     * <p><img src="..."></p> so we need to
     * enable inline true for image config.
     * Else, tiptap wont know what to do with
     * them and strip them out of the content.
     *
     * In future improvement, we can try block
     * users from dragging/pasting in raw images
     * and pop a "convert to asset" modal,
     * creating an inline-asset on the fly?
     *
     * API: Wraps all inline els in <p> to enforcing block level
     * API: Data URIs are not allowed in asset content - no base64
     *
     */
    extensions.push(
      Image.configure({
        inline: true,
        allowBase64: false,
      }),
    );

    extensions.push(HorizontalRuleMulti);
    extensions.push(ColonCommand);
    extensions.push(SlashCommand);
    extensions.push(
      BubbleMenu.configure({
        updateDelay: 500,
      }),
    );
    extensions.push(UnderlineExtended);
    extensions.push(
      SearchAndReplace.configure({
        searchResultClass: 'search-result',
        disableRegex: false,
      }),
    );
    extensions.push(
      Link.configure({
        openOnClick: false,
        autolink: false, // disable autolink
        HTMLAttributes: {
          rel: 'noopener noreferrer nofollow', // Security best practice
        },
      }),
    );

    extensions.push(TableExtended.configure({ resizable: false }));
    extensions.push(TableRowExtended);
    extensions.push(TableCaption);
    extensions.push(TableHeader);
    extensions.push(TableCell);
  }

  // Inline type extensions
  if (type === 'inline') {
    const isBylineField = name === 'byline'; // TNCMS-556561 API limits byline to specific formatting, so we disable some extensions for byline fields
    extensions.push(
      StarterKit.configure({
        document: false,
        undoRedo: false,
        paragraph: false,
        text: false,
        heading: isBylineField ? false : undefined,
        link: false,
        underline: false,
        bulletList: isBylineField ? false : undefined,
        orderedList: isBylineField ? false : undefined,
        blockquote: isBylineField ? false : undefined,
        horizontalRule: isBylineField ? false : undefined,
        dropcursor: disableDropCursor ? false : undefined,
        trailingNode: false,
      }),
    );
    if (isBylineField) {
      extensions.push(UnderlineExtended);
    } else {
      extensions.push(TextAlign.configure({ types: ['heading', 'paragraph'] }));
    }
    if (!isBylineField) {
      extensions.push(
        Link.configure({
          openOnClick: false,
          autolink: false, // disable autolink
          HTMLAttributes: {
            rel: 'noopener noreferrer nofollow', // Security best practice
          },
        }),
      );
      extensions.push(
        BubbleMenu.configure({
          updateDelay: 500,
        }),
      );
      extensions.push(UnderlineExtended);
      extensions.push(TableExtended.configure({ resizable: false }));
      extensions.push(TableRowExtended);
      extensions.push(TableCaption);
      extensions.push(TableHeader);
      extensions.push(TableCell);
    }
  }

  return extensions;
};

// Configure editor props
export const getEditorProps = (type?: EditorSetupConfig['type']): EditorProps => {
  const baseProps: EditorProps = {};

  switch (type) {
    case 'text':
    case 'textarea': {
      // Strip HTML from pasted content into plain text fields
      return {
        ...baseProps,
        transformPastedHTML(html: string) {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          return doc.body.textContent || '';
        },
      };
    }

    case 'number': {
      // Allow only numeric input and paste
      return {
        ...baseProps,
        handleTextInput(view, from, to, text) {
          // Allow processing if editor is in a composing state or number is entered
          return !(view.composing || /^[\d.-]*$/.test(text));
        },
        transformPastedHTML(html: string) {
          const pastedText =
            new DOMParser().parseFromString(html, 'text/html').body.textContent || '';
          return /^[\d.-]*$/.test(pastedText) ? pastedText : '';
        },
      };
    }

    case 'coordinate': {
      return {
        ...baseProps,
        handleTextInput(view, from, to, text) {
          // Only allow digits, decimal point (one only), and minus only at the start
          const currentText = view.state.doc.textContent;
          const newText = currentText.slice(0, from) + text + currentText.slice(to);
          // Allow only one hyphen at the start, followed by digits and at most one dot
          const isValid = /^-?\d*(\.\d*)?$/.test(newText);
          return !(view.composing || isValid);
        },
        transformPastedHTML(html: string) {
          const pastedText =
            new DOMParser().parseFromString(html, 'text/html').body.textContent || '';
          return extractCoordinate(pastedText);
        },
        transformPastedText(text: string) {
          return extractCoordinate(text);
        },
      };
    }

    default: {
      return baseProps;
    }
  }
};

/**
 * Helper to coerce potential editor content to a tiptap compatible format
 * some fields like priority, lat/long are numerical and should be converted to strings
 * before being used as initial content in the editor... preferably done in getEditorConfig
 * however just to be safe we will run all initial content through this
 * helper utility since tiptap doesnt warn or throw any errors if
 * the content isnt in the correct format currently, it will fail silently
 * with nothing being displayed in the field on the initial render...
 *
 * If the content is a number, convert it to a string.
 * If the content is undefined, return null for tiptap content.
 * Else return the content as-is: can be string, jsoncontent[], etc.
 *
 * @param content content to attempt to format
 * @returns {Content} formatted content (string, jsoncontent, jsoncontent[], or null)
 */

export function formatContent(content?: any): Content {
  if (typeof content === 'number') {
    return String(content); // coerce to string
  }

  if (content === undefined) {
    return null; // null for tiptap content
  }

  return content; // could be string, jsoncontent[], etc.
}

// see: https://github.com/yjs/y-utility?tab=readme-ov-file#ykeyvalue
// Note that this implementation does not support any nested Yjs types
// https://github.com/yjs/y-utility/blob/main/y-keyvalue.js#L12
export type NxtYArrConfig = { key: string; val: NxtYDocConfig };

// Fields on config may not exist on the ydoc at init time
// depending on if the ydoc was ever loaded / initialized to collab.
// Revision id is the bloxdoc `properties.current_revision.id` field.
// Check it at load time to determine if the ydoc needs to be updated from the bloxdoc
// When any client saves, a successful response from the save api
// should update the id with what the server returns.
export type NxtYDocConfig = {
  ydoc_revision_id?: number;
};

// Helper to determine if we should sync from blox or use cloud ydoc content.
type ShouldSyncFromBloxArgs = {
  syncedInitialYDocConfig?: SyncedInitialYDocConfigState;
  bloxDoc?: FullEditorialAsset;
};

export function shouldSyncFromBlox({
  syncedInitialYDocConfig,
  bloxDoc,
}: ShouldSyncFromBloxArgs): boolean {
  // Comparing by revision ids
  // If the ydoc doesnt have a revision use -1 for the comparision
  // as it makes it easier to compare if the ydoc is not setup with an id.
  const yDocRevisionId = syncedInitialYDocConfig?.ydoc_revision_id ?? -1;
  const bloxRevisionId = bloxDoc?.properties?.current_revision?.id ?? 0;

  // Just in case something weird happens (NaN, etc) validate the types...
  // Warn for logrocket visibility -- then fallback to sync from blox.
  const yRevIdValid = isNumber(yDocRevisionId) && Number.isFinite(yDocRevisionId);
  const bloxRevIdValid = isNumber(bloxRevisionId) && Number.isFinite(bloxRevisionId);
  if (!yRevIdValid) {
    console.warn('[shouldSyncFromBlox] ydoc-rev-id invalid:', yDocRevisionId);
    return true;
  }
  if (!bloxRevIdValid) {
    console.warn('[shouldSyncFromBlox] blox-rev-id invalid:', bloxRevisionId);
    return true;
  }

  // Compare which revision is newer. Only if the blox revision is
  // newer, set the content from blox. If the blox revision id is
  // the same (or somehow older), then use the cloud contents.
  const bloxIsNewer = bloxRevisionId > yDocRevisionId;
  const shouldSetBloxContent = Boolean(bloxIsNewer);
  return shouldSetBloxContent;
}

/**
 * Update the ydoc_revision_id field in the yDoc config.
 * Returns true if the update was successful, false otherwise.
 * Throws if arguments are missing or invalid.
 */

type SetYDocConfigMetaArgs = {
  yDoc?: Y.Doc;
  bloxDoc?: FullEditorialAsset;
};

// success/failure bool
type SetYDocConfigMetaResult = boolean;

export function setYDocConfigMeta({
  yDoc,
  bloxDoc,
}: SetYDocConfigMetaArgs): SetYDocConfigMetaResult {
  try {
    if (!yDoc) {
      throw new Error('YDoc not provided.');
    }

    if (!bloxDoc) {
      throw new Error('BloxDoc not provided.');
    }

    const bloxRevisionId = bloxDoc?.properties?.current_revision?.id;
    const bloxRevIdValid = isNumber(bloxRevisionId) && Number.isFinite(bloxRevisionId);

    if (!bloxRevIdValid) {
      throw new Error('BloxDoc revision id is invalid.');
    }

    // This looks weird, getArray, then a kv map, then set kv to arr...
    // but is the recommended way via y-utility docs, for perf reasons.
    // see: https://github.com/yjs/y-utility?tab=readme-ov-file#ykeyvalue
    // Note that this implementation does not support any nested Yjs types
    // https://github.com/yjs/y-utility/blob/main/y-keyvalue.js#L12
    const yarr = yDoc.getArray<NxtYArrConfig>(NXT_YDOC_CONFIG);
    const ykv = new YKeyValue<NxtYDocConfig>(yarr);
    const config = ykv.get(NXT_YDOC_CONFIG);
    const configCopy = config ? clone(config) : {};
    const currYDocRevId = configCopy?.ydoc_revision_id;

    // No need to update if values are identical
    // eg. multiple concurrent hook mounts or unchanged
    if (currYDocRevId === bloxRevisionId) {
      return true; // consider success...
    }

    // update ydoc config and leave rest as-is
    // eg: {..., ydoc_revision_id: 42 }
    const updated: NxtYDocConfig = {
      ...configCopy,
      ydoc_revision_id: bloxRevisionId,
    };

    // dispatch a write to the ydoc
    ykv.set(NXT_YDOC_CONFIG, updated);

    // Temporary logging for logrocket...
    console.log('[setYDocConfigMeta] Set YDoc RevID:', updated?.ydoc_revision_id);
    return true; // success
  } catch (e) {
    console.error('[setYDocConfigMeta]', e);
    return false; // failure
  }
}

// Undocumented - https://github.com/ueberdosis/tiptap/issues/3686
// Way to detect if change came in from yjs collab or not...
// type TiptapYSyncMeta = { isChangeOrigin?: boolean; binding?: ProsemirrorBinding;... };
// const ySyncMeta = transaction.getMeta('y-sync$') as TiptapYSyncMeta;
// const ySyncEventOrigin = isChangeOrigin(transaction); // prosemirror helper fn
// https://github.com/yjs/y-prosemirror/blob/master/src/plugins/keys.js
// https://github.com/ueberdosis/tiptap/blob/main/packages/extension-collaboration/src/helpers/isChangeOrigin.ts
// https://github.com/ueberdosis/tiptap/tree/main/packages/extension-collaboration

export interface UseCreateEditorParams extends UseEditorOptions {
  editorConfig?: EditorSetupConfig;
  sessionConfig?: TSessionConfigData;
  // --- collab props ---
  collabProvider?: CollabProviderState;
  collabUser?: CollabUserInfo;
  syncedInitialYDocConfig?: SyncedInitialYDocConfigState;
  bloxDoc?: FullEditorialAsset;
  appSettings?: AppSettingsCtxType['appSettings']; // Global & Site scope.
  // optional fns merged in if needed at definition
  // time in order to not override default props, but
  // can still attach handlers via editor.on('x') too
  onCreateFn?: (props: EditorEvents['create']) => void;
  onUpdateFn?: (props: EditorEvents['update']) => void;
  onContentErrorFn?: (props: EditorEvents['contentError']) => void;
  // Temp logging for logrocket...
  withLogging?: boolean;
}

type TiptapJson = JSONContent | null;
type TiptapHtml = string | null;
type TiptapText = string | null;

// cloud = used collab ydoc directly
// bloxsync = cloud was outdated and blox doc was newer, overwriting cloud doc
// blox = used blox record (or cloud was outdated, so blox overwrite used)
// content is basically from blox if state is: bloxsync or blox
export type InitViaState = 'cloud' | 'bloxsync' | 'blox' | null;

export type UseCreateEditorReturn = {
  editor: Editor | null; // tiptap editor instance
  initVia: InitViaState; // where initial content came from
  // note onCreate event is only emitted once, so other onCreate handlers registering
  // to the editor might cause a race condition if attached in deeper components so
  // we can also use hasSetInitContent to detect if onCreate ran and ready.
  hasSetInitContent: boolean; // if initial content is set
  // onCreate runs get editor.getHtml(), etc and set to states
  // please note in collab mode since this is a websocket stream
  // and it is not an awaitable, whatever is streamed in at the
  // time of onCreate call is what will be set to html/json/text created
  // states -- may be an edge case with partial info if too slow...?
  htmlCreated: TiptapHtml; // html oncreate / null if not set yet
  jsonCreated: TiptapJson; // json oncreate
  textCreated: TiptapText; // text oncreate
  // for manual autosave diff purposes - can store a state here for
  // manually setting the last saved editor.getHtml()
  // during diff compare can check against this state if utilizing it.
  // Alternatively use blox as the source of truth to detect changes
  // run it through generateTiptapHtml -> compareTiptapHtml
  // to get a boolean indicating if the current state of the editor
  // has changes that need to be saved. More info in AutoSave Component.
  lastSavedHtml: TiptapHtml;
  setLastSavedHtml: (html: TiptapHtml) => void;
};

// Helper to handle setting local states on editor create...
type HandleOnCreateStateArgs = {
  editor: Editor;
  via: InitViaState;
  setYDocRevisionArgs?: {
    yDoc?: Y.Doc;
    bloxDoc?: FullEditorialAsset;
  };
};

/**
 * CMSADMIN-2367 - undoState / yUndoPluginKey
 * clear undo stack so initial collab content cant be undone
 * note this is a workaround due to ephemeral ydoc storage
 * and tiptap does not provide a direct way to prevent
 * history in the collaboration extension...
 * Not much documentation on this - see related info:
 * https://github.com/ueberdosis/tiptap/discussions/2125
 * https://github.com/ueberdosis/tiptap/discussions/5708
 * node_modules\@tiptap\extension-collaboration\dist\index.d.ts
 * node_modules\@tiptap\y-tiptap\dist\y-tiptap.js
 * node_modules\prosemirror-state\dist\index.d.ts
 * node_modules\yjs\src\utils\UndoManager.js
 */

type YUndoPluginStateType = UndoPluginState | undefined;

/**
 * useCreateEditor hook is focused on setting up editors for editorial,
 * and handling workarounds for the collaboration modes...
 *
 * It extends tiptaps useEditor hook, but for the majority of basic cases
 * can instead directly use the useEditor hook that tiptap provides
 * in combination with a few utility functions that this hook uses internally
 * as this has a lot of additional logic that is specific to collab setup.
 */
export function useCreateEditor(
  params: UseCreateEditorParams = {},
  editorDeps: DependencyList = [],
): UseCreateEditorReturn {
  const {
    editorConfig,
    collabProvider,
    collabUser,
    sessionConfig,
    syncedInitialYDocConfig,
    bloxDoc,
    appSettings,
    onCreateFn,
    onUpdateFn,
    onContentErrorFn,
    withLogging = false,
    ...restEditorProps
  } = params;

  const [hasSetInitContent, setHasSetInitContent] = useState<boolean>(false);
  const [initVia, setInitVia] = useState<InitViaState>(null);
  const [jsonCreated, setJsonCreated] = useState<TiptapJson>(null);
  const [htmlCreated, setHtmlCreated] = useState<TiptapHtml>(null);
  const [textCreated, setTextCreated] = useState<TiptapText>(null);
  const [lastSavedHtml, setLastSavedHtml] = useState<TiptapHtml>(null);

  const memoEditorDeps = useMemo(() => editorDeps, [editorDeps]);

  // collab provider exists and initial ydoc config exists
  const collabModeAndSynced = useMemo(() => {
    return Boolean(collabProvider && syncedInitialYDocConfig);
  }, [collabProvider, syncedInitialYDocConfig]);

  const initialContent = useMemo(() => {
    return formatContent(editorConfig?.contentForEditor);
  }, [editorConfig]);

  const buildExts = useMemo(
    () =>
      getEditorExtensions({
        editorConfig: editorConfig,
        sessionConfig: sessionConfig,
        collabProvider: collabProvider,
        collabUser: collabUser,
        appSettings: appSettings,
      }),
    [editorConfig, collabProvider, collabUser, sessionConfig, appSettings],
  );

  const buildEditorProps = useMemo(
    () => getEditorProps(editorConfig?.type),
    [editorConfig],
  );

  const handleOnCreateStates = useCallback(
    ({ editor, via, setYDocRevisionArgs }: HandleOnCreateStateArgs) => {
      try {
        setJsonCreated(editor.getJSON());
        setHtmlCreated(editor.getHTML());
        setTextCreated(editor.getText());
        // via: direct blox content, cloud, or bloxsync overwrite
        setInitVia(via);
        // If provided, update ydoc revision data
        if (setYDocRevisionArgs) {
          setYDocConfigMeta({
            yDoc: setYDocRevisionArgs?.yDoc,
            bloxDoc: setYDocRevisionArgs?.bloxDoc,
          });
        }
        // mark initial contents have been set
        setHasSetInitContent(true);
      } catch (error) {
        console.error('[UseCE] handleInitStates:', error);
      }
    },
    [],
  );

  // Temporary helper for logrocket
  const sendDebugLog = useCallback(
    (message: string) => {
      /**
       * This is being added to avoid errornous logs during jest tests until all collab issues are resolved
       * the collab logging is not relevant during jest tests and clutters the output
       * so we will only log if not in a jest environment
       */
      if (withLogging && typeof jest === 'undefined') {
        const editorName = editorConfig?.name || editorConfig?.id || '(unnamed)';
        console.log(`[UseCE] ${editorName}:`, message);
      }
    },
    [editorConfig?.name, editorConfig?.id, withLogging],
  );

  /**
   * Note: Initial setContent emitUpdate...
   * Setting true has some impact on initial asset form validation triggers.
   * eg if AssetForm mode="onChange", and in create mode with blank and fields,
   * if in the onCreate setContent and emit:true, it is possible that
   * useDebouncedOnChange will pick this up as a change and process it
   * which causes the form to trigger its validate on change.
   *
   * Unfortunatley react-hook-form mode detects this as a real change
   * since its attempting to change the form values, even if the value
   * is the same as what exists in the form state.
   *
   * For this reason we can leave it as false when not in collab
   * mode so only modified fields get updated and validated onChange.
   * When in collab mode, I think we should always emit the update,
   * since may load from cloud or bloxdoc, and we may need to
   * emit update to other users connected the ydoc / listeners.
   */

  const editor = useEditor(
    {
      content: undefined, // avoid content duplication
      editable: false, // effect will update once content setup
      extensions: buildExts,
      editorProps: buildEditorProps,
      immediatelyRender: true,
      enableContentCheck: false, // see note in onContentError CMSADMIN-1210
      shouldRerenderOnTransaction: false, // performance boost
      onCreate: async (params) => {
        // fwd call if provided
        if (onCreateFn) {
          onCreateFn(params);
        }
        const { editor } = params;

        // not in collab can set the content immediately
        if (!collabProvider) {
          try {
            const success = editor
              .chain()
              .setContent(initialContent, {
                emitUpdate: false,
                errorOnInvalidContent: false, // note in onContentError CMSADMIN-1210
              })
              .setMeta('addToHistory', false) // not undoable
              .setMeta('bloxInitContent', true) // detect our init content txns
              .run();

            if (success) {
              sendDebugLog('base: blox');
              // initial content set from blox record
              handleOnCreateStates({ editor: editor, via: 'blox' });
            }
          } catch (error) {
            console.error('[UseCE] base onCreate:', error);
          }
        }

        // collab mode: provider exists and we have a copy of the
        // initial ydoc config for revision id comparisons to determine
        // set content from bloxdoc, or skip and use collab cloud contents...
        if (collabModeAndSynced) {
          try {
            // determine flow by revision id comparison
            const setBloxContent = shouldSyncFromBlox({
              syncedInitialYDocConfig: syncedInitialYDocConfig,
              bloxDoc: bloxDoc,
            });

            // if blox considered newer than cloud contents
            // set/replace contents in the ydoc with blox data
            if (setBloxContent) {
              const success = editor
                .chain()
                .setContent(initialContent, {
                  emitUpdate: true,
                  errorOnInvalidContent: false,
                })
                .setMeta('addToHistory', false)
                .setMeta('bloxInitContent', true)
                .run();

              if (success) {
                // CMSADMIN-2367 - notes on typedef of YUndoPluginStateType
                // Workaround to clear undo stack after initial collab content load
                const undoState = yUndoPluginKey.getState(
                  editor.state,
                ) as YUndoPluginStateType;
                undoState?.undoManager?.clear();

                sendDebugLog('collab: overwrite');
                // bloxsync - cloud ydoc is outdated, so this
                // editor overwrote the ydoc with blox contents
                // As this is overwriting with blox data we need to
                // update ydoc revision id to the bloxdocs revision id...
                handleOnCreateStates({
                  editor: editor,
                  via: 'bloxsync',
                  setYDocRevisionArgs: {
                    yDoc: collabProvider?.document,
                    bloxDoc: bloxDoc,
                  },
                });
              }
            } else {
              // ydoc cloud contents are deemed to be newer
              // dont overwrite and use it as-is instead
              sendDebugLog('collab: cloud');
              handleOnCreateStates({ editor: editor, via: 'cloud' });
            }
          } catch (error) {
            console.error('[UseCE] collab onCreate:', error);
          }
        }
      },
      onUpdate: async (params) => {
        if (onUpdateFn) {
          onUpdateFn(params);
        }
      },
      onContentError: async (params) => {
        if (onContentErrorFn) {
          onContentErrorFn(params);
        }
        const { error } = params;
        // CMSADMIN-1210
        // https://tiptap.dev/docs/guides/invalid-schema#for-collaborative-editing
        // note: onContentError seemed inconsistently emitted in some cases...
        // We will need to resolve the issue with invalid schema
        // tables before we can safely enable this or
        // errorOnInvalidContent or enableContentCheck
        // as tiptap will not set content if it detects an error...
        console.error('[UseCE] onContentError:', error);
      },
      // spread props will overwrite above defaults if dev desires
      ...restEditorProps,
    },
    memoEditorDeps,
  );

  // Since we cant async/await the first collab onSynced easily
  // for safety we should initialize as not editable
  // and then once the editor is created and initial content has been
  // determined and set in place, then we can dynamically set editable
  // to whatever the config would have been for this editor...
  const isEditableConfig = useMemo(() => editorConfig?.editable ?? true, [editorConfig]);

  // Set the editable option based on the config setting once we
  // have set the initial content and the editor has been instantiated
  useEffect(() => {
    if (editor && hasSetInitContent) {
      editor.setEditable(isEditableConfig, true);
    }
  }, [editor, isEditableConfig, hasSetInitContent]);

  const result = useMemo(() => {
    return {
      editor: editor,
      hasSetInitContent: hasSetInitContent,
      jsonCreated: jsonCreated,
      htmlCreated: htmlCreated,
      textCreated: textCreated,
      initVia: initVia,
      lastSavedHtml: lastSavedHtml,
      setLastSavedHtml: setLastSavedHtml,
    };
  }, [
    editor,
    hasSetInitContent,
    jsonCreated,
    htmlCreated,
    textCreated,
    initVia,
    lastSavedHtml,
    setLastSavedHtml,
  ]);

  return result;
}

/**
 * Uncontrolled Version
 *
 * strongly prefer controlled mode version for collab mode
 *
 * In most cases you will want to use the controlled hook version
 * to keep the editor in sync with reacts render cycles and get
 * all the benefits that the hook provides automatically.
 *
 * In certain edge cases you may need to use the uncontrolled
 * plain "new Editor" instance, this function will help, but you
 * are fully responsible for managing the editor instances yourself
 * and ensuring it is synced up with the react component lifecycle.
 *
 * For example, you can create uncontrolled editors in a useEffect or
 * useMemo and then have the deps there manage the lifecycle
 * and perhaps keeping the contents/etc synced to some state/map obj
 * that you can update via attached editor onUpdate handler effects.
 *
 */

interface CreateUncontrolledEditorParams extends UseCreateEditorParams {}

// TPartial as uncontrolled version does not recieve editor/via args
type PartialHandleOnCreateStateArgs = Partial<HandleOnCreateStateArgs>;

export function createUncontrolledEditor({
  editorConfig,
  collabProvider,
  collabUser,
  sessionConfig,
  syncedInitialYDocConfig,
  bloxDoc,
  onCreateFn,
  onUpdateFn,
  onContentErrorFn,
  withLogging = false,
  ...restEditorProps
}: CreateUncontrolledEditorParams) {
  // some fields like priority or numerical must be strings for init content
  const initialContent = formatContent(editorConfig?.contentForEditor);

  const isEditable = editorConfig?.editable ?? true;

  const buildEditorProps = getEditorProps(editorConfig?.type);

  // collab provider exists and initial ydoc config exists
  const collabModeAndSynced = Boolean(collabProvider && syncedInitialYDocConfig);

  const buildExts = getEditorExtensions({
    editorConfig: editorConfig,
    collabProvider: collabProvider,
    collabUser: collabUser,
    sessionConfig: sessionConfig,
  });

  const handleOnCreateStates = ({
    setYDocRevisionArgs,
  }: PartialHandleOnCreateStateArgs) => {
    try {
      // If provided, update ydoc revision data
      if (setYDocRevisionArgs) {
        setYDocConfigMeta({
          yDoc: setYDocRevisionArgs?.yDoc,
          bloxDoc: setYDocRevisionArgs?.bloxDoc,
        });
      }
    } catch (error) {
      console.error('[uncontrolledEditor] handleOnCreateStates:', error);
    }
  };

  // Temporary helper for logrocket
  const sendDebugLog = (message: string) => {
    /**
     * This is being added to avoid errornous logs during jest tests until all collab issues are resolved
     * the collab logging is not relevant during jest tests and clutters the output
     * so we will only log if not in a jest environment
     */
    if (withLogging && typeof jest === 'undefined') {
      const editorName = editorConfig?.name || editorConfig?.id || '(unnamed)';
      console.log(`[uncontrolledEditor] ${editorName}:`, message);
    }
  };

  // Note: onCreate, onUpdate, etc must be very similar
  // to the useCreateEditor hook callbacks so if
  // modifying them please update both places and see
  // comments in the useCreateEditor hook for reference

  const editor = new Editor({
    content: undefined,
    editable: isEditable,
    extensions: buildExts,
    editorProps: buildEditorProps,
    onCreate: async (params) => {
      if (onCreateFn) {
        onCreateFn(params);
      }
      const { editor } = params;

      if (!collabProvider) {
        try {
          const success = editor
            .chain()
            .setContent(initialContent, {
              emitUpdate: false,
              errorOnInvalidContent: false,
            })
            .setMeta('addToHistory', false) // not undoable
            .setMeta('bloxInitContent', true) // detect our init content txns
            .run();

          if (success) {
            // uncontrolled doesnt need to update ydoc in this mode
            sendDebugLog('base: blox');
          }
        } catch (error) {
          console.error('[uncontrolledEditor] base onCreate:', error);
        }
      }

      // collab mode: provider exists and we have a copy of the initial ydoc config
      if (collabModeAndSynced) {
        try {
          // determine flow by revision id comparison
          const setBloxContent = shouldSyncFromBlox({
            syncedInitialYDocConfig,
            bloxDoc,
          });

          // overwrite ydoc with blox content, or use cloud doc as-is
          if (setBloxContent) {
            const success = editor
              .chain()
              .setContent(initialContent, {
                emitUpdate: true,
                errorOnInvalidContent: false,
              })
              .setMeta('addToHistory', false)
              .setMeta('bloxInitContent', true)
              .run();

            if (success) {
              // CMSADMIN-2367 - notes on typedef of YUndoPluginStateType
              // Workaround to clear undo stack after initial collab content load
              const undoState = yUndoPluginKey.getState(
                editor.state,
              ) as YUndoPluginStateType;
              undoState?.undoManager?.clear();

              sendDebugLog('collab: overwrite');
              // mark content set and update ydoc revision id
              handleOnCreateStates({
                setYDocRevisionArgs: {
                  yDoc: collabProvider?.document,
                  bloxDoc: bloxDoc,
                },
              });
            }
          } else {
            // else: ydoc cloud contents are deemed to be newer
            // dont overwrite and use it as-is instead
            sendDebugLog('collab: cloud');
          }
        } catch (error) {
          console.error('[uncontrolledEditor] collab onCreate:', error);
        }
      }
    },
    onUpdate: async (params) => {
      if (onUpdateFn) {
        onUpdateFn(params);
      }
    },
    onContentError: async (params) => {
      if (onContentErrorFn) {
        onContentErrorFn(params);
      }
      const { error } = params;
      console.error('[uncontrolledEditor] onContentError:', error);
    },
    // spread will overwrite above defaults if dev desires
    ...restEditorProps,
  });

  return editor;
}

/**
 *
 * File Notes:
 *
 * Generic component examples for using the custom editor hook
 * as a component would so you can map/loop over the editor
 * without breaking the rules of hooks. Alternatively uncontrolled version
 * can pass in the editor directly as a prop and create a map of editors
 * corresponding into each lower level <EditorContent> instance.
 *
 * type EditorContentComponentProps = Omit<
 *   React.ComponentProps<typeof EditorContent>,
 *   'editor'
 * >;
 *
 * interface CustomTiptapEditorProps {
 *   customHookProps?: UseCreateEditorParams;
 *   customHookDeps?: DependencyList;
 *   editorContentProps?: EditorContentComponentProps;
 * }
 * export const CustomTiptapEditor = memo(
 *   ({ customHookProps, customHookDeps, editorContentProps }: CustomTiptapEditorProps) => {
 *     const memoDeps = useMemo(() => customHookDeps, [customHookDeps]);
 *     const editor = useCreateEditor(customHookProps, memoDeps); // custom hook
 *     return (
 *       <EditorContent
 *         editor={editor}
 *         {...editorContentProps}
 *       />
 *     );
 *   },
 * );
 * CustomTiptapEditor.displayName = 'CustomTiptapEditor';
 *
 *
 */
